/**
 * OnboardingTestPortal Component
 * Test interface for the client onboarding process
 * Allows admins to preview and test onboarding flow without generating secure links
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormsByPurpose } from '../../../hooks/useSecureForms';
import styles from './OnboardingTestPortal.module.css';

const OnboardingTestPortal = () => {
    const navigate = useNavigate();
    const { forms: onboardingForms, loading: formsLoading } = useFormsByPurpose('client_onboarding');
    const [selectedFormId, setSelectedFormId] = useState('');
    const [testMode, setTestMode] = useState('preview'); // 'preview' or 'mock'

    /**
     * Generate a mock token for testing
     */
    const generateMockToken = () => {
        return `test_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    };

    /**
     * Open onboarding welcome page in test mode
     */
    const handleTestWelcomePage = () => {
        const mockToken = generateMockToken();
        // Store test mode flag in sessionStorage
        sessionStorage.setItem('onboarding_test_mode', 'true');
        sessionStorage.setItem('onboarding_test_form_id', selectedFormId || 'default');
        
        // Open in new tab
        window.open(`/client-onboarding/${mockToken}`, '_blank');
    };

    /**
     * Open onboarding form directly in test mode
     */
    const handleTestFormPage = () => {
        const mockToken = generateMockToken();
        sessionStorage.setItem('onboarding_test_mode', 'true');
        sessionStorage.setItem('onboarding_test_form_id', selectedFormId || 'default');
        
        // Open in new tab
        window.open(`/client-onboarding/${mockToken}/form`, '_blank');
    };

    /**
     * Open full flow in current window
     */
    const handleTestFullFlow = () => {
        const mockToken = generateMockToken();
        sessionStorage.setItem('onboarding_test_mode', 'true');
        sessionStorage.setItem('onboarding_test_form_id', selectedFormId || 'default');
        
        navigate(`/client-onboarding/${mockToken}`);
    };

    /**
     * Clear test mode
     */
    const handleClearTestMode = () => {
        sessionStorage.removeItem('onboarding_test_mode');
        sessionStorage.removeItem('onboarding_test_form_id');
        alert('Test mode cleared!');
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2>🧪 Test Client Onboarding Process</h2>
                <p className={styles.subtitle}>
                    Preview and test the client-facing onboarding experience without creating real secure links
                </p>
            </div>

            <div className={styles.infoCard}>
                <div className={styles.infoIcon}>ℹ️</div>
                <div className={styles.infoContent}>
                    <h3>About Test Mode</h3>
                    <p>
                        This portal allows you to test the complete client onboarding experience. 
                        Test links are temporary and won't send emails or create real records.
                    </p>
                </div>
            </div>

            <div className={styles.section}>
                <h3>Select Form (Optional)</h3>
                <p className={styles.sectionDescription}>
                    Choose a form template to test, or leave empty to use default settings
                </p>
                
                {formsLoading ? (
                    <div className={styles.loading}>Loading forms...</div>
                ) : (
                    <div className={styles.formSelector}>
                        <select
                            value={selectedFormId}
                            onChange={(e) => setSelectedFormId(e.target.value)}
                            className={styles.select}
                        >
                            <option value="">-- Default Form (Auto-detect) --</option>
                            {onboardingForms && onboardingForms.map((form) => (
                                <option key={form.formId} value={form.formId}>
                                    {form.title || form.name || 'Untitled Form'}
                                    {form.isDefault && ' (Default)'}
                                </option>
                            ))}
                        </select>
                        {selectedFormId && (
                            <button
                                onClick={() => setSelectedFormId('')}
                                className={styles.clearButton}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.section}>
                <h3>Test Options</h3>
                <div className={styles.testOptions}>
                    <div className={styles.testCard}>
                        <div className={styles.testCardIcon}>🏠</div>
                        <h4>Welcome Page Only</h4>
                        <p>Test the initial landing page with WETLAB logo and welcome message</p>
                        <button
                            onClick={handleTestWelcomePage}
                            className={styles.testButton}
                        >
                            Open Welcome Page
                        </button>
                    </div>

                    <div className={styles.testCard}>
                        <div className={styles.testCardIcon}>📋</div>
                        <h4>Form Page Only</h4>
                        <p>Test the onboarding form directly (skips welcome page)</p>
                        <button
                            onClick={handleTestFormPage}
                            className={styles.testButton}
                        >
                            Open Form Page
                        </button>
                    </div>

                    <div className={styles.testCard}>
                        <div className={styles.testCardIcon}>🚀</div>
                        <h4>Complete Flow</h4>
                        <p>Test the full onboarding experience from start to finish</p>
                        <button
                            onClick={handleTestFullFlow}
                            className={styles.testButtonPrimary}
                        >
                            Start Full Flow
                        </button>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <h3>Instructions</h3>
                <div className={styles.instructions}>
                    <div className={styles.step}>
                        <div className={styles.stepNumber}>1</div>
                        <div className={styles.stepContent}>
                            <strong>Select a Form Template</strong> (optional) or use the default
                        </div>
                    </div>
                    <div className={styles.step}>
                        <div className={styles.stepNumber}>2</div>
                        <div className={styles.stepContent}>
                            <strong>Choose a Test Option</strong> above to open the onboarding process
                        </div>
                    </div>
                    <div className={styles.step}>
                        <div className={styles.stepNumber}>3</div>
                        <div className={styles.stepContent}>
                            <strong>Test the Experience</strong> as if you were a client receiving the link
                        </div>
                    </div>
                    <div className={styles.step}>
                        <div className={styles.stepNumber}>4</div>
                        <div className={styles.stepContent}>
                            <strong>Submit or Exit</strong> - Test data won't be saved to the system
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <h3>Utilities</h3>
                <div className={styles.utilities}>
                    <button
                        onClick={handleClearTestMode}
                        className={styles.utilityButton}
                    >
                        Clear Test Mode
                    </button>
                    <button
                        onClick={() => navigate('/configurations')}
                        className={styles.utilityButton}
                    >
                        Back to Configurations
                    </button>
                </div>
            </div>

            <div className={styles.warningCard}>
                <div className={styles.warningIcon}>⚠️</div>
                <div className={styles.warningContent}>
                    <strong>Note:</strong> Test mode is for preview only. No emails will be sent, 
                    and form submissions will not create real client records.
                </div>
            </div>
        </div>
    );
};

export default OnboardingTestPortal;
